import React from "react";
import { Dialog, DialogContent, Box, Typography, Button, TextField, useMediaQuery } from "@mui/material";
import theme from "../../util/theme/theme";
import BottomSheetDialog from "./mobile/mobile-bottom-sheet";

export interface HardDeleteModalProps {
    open: boolean;
    image?: { src: string; width?: number; height?: number };
    message?: string;
    red?: boolean;
    description?: string;
    footer?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    notesLabel?: string;
    notesPlaceholder?: string;
    notesRequired?: boolean;
    notes?: string;
    onNotesChange?: (notes: string) => void;
    onConfirm: () => void;
    onCancel: () => void;
}

const HardDeleteModal: React.FC<HardDeleteModalProps> = ({
    open,
    image,
    message = "Are you sure?",
    red = true,
    footer,
    description = "This action cannot be undone.",
    confirmLabel = "Hard Delete",
    cancelLabel = "Cancel",
    notesLabel = "Notes",
    notesPlaceholder = "Enter reason for hard delete...",
    notesRequired = false,
    notes = "",
    onNotesChange,
    onConfirm,
    onCancel,
}) => {
    const isConfirmDisabled = notesRequired && !notes?.trim();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const content = (
        <>
            <DialogContent sx={{ p: 0, pt: 3, pb: 3, px: 4, textAlign: 'center', minWidth: isMobile ? 'auto' : 340 }}>
                {image && (
                    <Box mb={2} display="flex" justifyContent="center">
                        <img
                            src={image.src}
                            width={image.width || 140}
                            height={image.height || 120}
                            alt="delete"
                            style={{ objectFit: 'contain', maxWidth: '100%' }}
                        />
                    </Box>
                )}
                <Typography variant="h6" fontWeight={700} mb={1.5} color={red ? "#F44336" : "inherit"}>
                    {message}
                </Typography>
                <Typography
                    color="text.secondary"
                    fontSize={16}
                    mb={2}
                    component="div"
                    dangerouslySetInnerHTML={description ? { __html: description } : undefined}
                />
                {footer && (
                    <Typography fontWeight={600} mb={2} color="text.secondary">
                        {footer}
                    </Typography>
                )}
                <Box mb={3} textAlign="left">
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label={notesLabel}
                        placeholder={notesPlaceholder}
                        value={notes}
                        onChange={(e) => onNotesChange?.(e.target.value)}
                        required={notesRequired}
                        variant="outlined"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1.5,
                            },
                        }}
                    />
                </Box>
                <Button
                    variant="contained"
                    color="error"
                    fullWidth
                    disabled={isConfirmDisabled}
                    sx={{ borderRadius: 2, fontWeight: 600, fontSize: 17, py: 1.2, mb: 1.5, boxShadow: 'none', textTransform: 'none', color: '#fff' }}
                    onClick={onConfirm}
                >
                    {confirmLabel}
                </Button>
                <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    sx={{ borderRadius: 2, fontWeight: 600, fontSize: 17, py: 1.2, textTransform: 'none' }}
                    onClick={onCancel}
                >
                    {cancelLabel}
                </Button>
            </DialogContent>
        </>
    );

    return isMobile ? (
        <BottomSheetDialog open={open} onClose={onCancel} fitContent>
            {content}
        </BottomSheetDialog>
    ) : (
        <Dialog open={open} onClose={onCancel} maxWidth="xs" PaperProps={{ sx: { borderRadius: 4, p: 0 } }}>
            {content}
        </Dialog>
    );
};

export default HardDeleteModal;
